import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Monitor, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/i18n";
import { Field, Tag } from "@/components/app/kit";
import { useAuth } from "@/context/AuthContext";

const PasswordForm = () => {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) return toast.error("Les mots de passe ne correspondent pas");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Mot de passe modifié. Vos autres sessions ont été fermées.");
      setF({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <Field label="Mot de passe actuel"><Input type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="account-current-password" required /></Field>
      <Field label="Nouveau mot de passe" hint="10 caractères minimum, avec au moins une lettre et un chiffre"><Input type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="account-new-password" required /></Field>
      <Field label="Confirmation"><Input type="password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid="account-confirm-password" required /></Field>
      <Button type="submit" disabled={busy} data-testid="account-password-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Mettre à jour</Button>
    </form>
  );
};

const Sessions = () => {
  const { logout } = useAuth();
  const [items, setItems] = useState(null);
  const load = () => api.get("/auth/sessions").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => {
    load();
  }, []);
  const revoke = async (id) => {
    await api.delete(`/auth/sessions/${id}`);
    toast.success("Session révoquée");
    load();
  };
  if (!items) return <Loader2 className="h-5 w-5 animate-spin text-sky-600" />;
  return (
    <div className="space-y-3">
      {items.map((s) => (
        <div key={s.id} className="flex items-center gap-3 rounded-lg border border-slate-200 p-3" data-testid="account-session-row">
          <Monitor className="h-4 w-4 text-slate-400 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-xs text-slate-700 truncate">{s.user_agent || "Appareil inconnu"}</p>
            <p className="text-[11px] text-slate-400">{s.ip} · ouverte le {fmtDateTime(s.created_at)}</p>
          </div>
          {s.current ? <Tag tone="green">Session actuelle</Tag> : <Button size="sm" variant="outline" onClick={() => revoke(s.id)} data-testid="account-session-revoke">Révoquer</Button>}
        </div>
      ))}
      <Button variant="destructive" size="sm" onClick={async () => { await api.post("/auth/logout-all"); logout(); }} data-testid="account-logout-all">
        Déconnecter toutes les sessions
      </Button>
    </div>
  );
};

export const AccountDialog = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg" data-testid="account-dialog">
      <DialogHeader>
        <DialogTitle>Mon compte et sécurité</DialogTitle>
        <DialogDescription>Gérez votre mot de passe et vos sessions actives.</DialogDescription>
      </DialogHeader>
      <Tabs defaultValue="password">
        <TabsList className="mb-4">
          <TabsTrigger value="password" data-testid="account-tab-password">Mot de passe</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="account-tab-sessions">Sessions</TabsTrigger>
        </TabsList>
        <TabsContent value="password"><PasswordForm /></TabsContent>
        <TabsContent value="sessions">{open && <Sessions />}</TabsContent>
      </Tabs>
    </DialogContent>
  </Dialog>
);
